import { Switch, Route, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Ruler, LogOut, Users, CreditCard, ChevronDown, Crown, Shield, UserCheck, Eye, LayoutGrid, Package, Settings, Plus, Menu, Clock, UserSquare2, CalendarDays } from "lucide-react";
import { useState, useEffect, lazy, Suspense } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import NotFound from "@/pages/not-found";
import { AuthProvider, RequireAuth, useAuth } from "@/lib/auth";
import { ErrorBoundary } from "@/components/error-boundary";
import { Spinner } from "@/components/ui/spinner";
const Dashboard = lazy(() => import("./pages/dashboard"));
const Estimate = lazy(() => import("./pages/estimate"));
const ClientView = lazy(() => import("./pages/client-view"));
const Catalog = lazy(() => import("./pages/catalog"));
const SchedulePage = lazy(() => import("./pages/schedule"));
const SettingsPage = lazy(() => import("./pages/settings"));
const Login = lazy(() => import("./pages/login"));
const Register = lazy(() => import("./pages/register"));
const ForgotPassword = lazy(() => import("./pages/forgot-password"));
const ResetPassword = lazy(() => import("./pages/reset-password"));
const InvitePage = lazy(() => import("./pages/invite"));
const TeamPage = lazy(() => import("./pages/team"));
const AdminPage = lazy(() => import("./pages/admin"));
const ClientsPage = lazy(() => import("./pages/clients"));
const ClientProfile = lazy(() => import("./pages/client-profile"));
const SharedPlanPage = lazy(() => import("./pages/shared-plan"));
const DevSiteDesigner = lazy(() => import("./pages/dev-site-designer"));

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  requireCan?: string;
}

const SIDEBAR_NAV: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Workspace',
    items: [
      { href: '/', label: 'Projects', icon: LayoutGrid },
      { href: '/estimate/new', label: 'New Estimate', icon: Plus, requireCan: 'create_project' },
      { href: '/clients', label: 'Clients', icon: UserSquare2 },
      { href: '/schedule', label: 'Schedule', icon: CalendarDays },
      { href: '/catalog', label: 'Catalog', icon: Package },
    ],
  },
  {
    label: 'Account',
    items: [
      { href: '/settings', label: 'Settings', icon: Settings },
      { href: '/settings/team', label: 'Team', icon: Users, requireCan: 'manage_team' },
    ],
  },
];

const ROLE_ICONS: Record<string, React.ReactNode> = {
  owner: <Crown className="h-3 w-3" style={{ color: 'var(--color-amber-400)' }} />,
  admin: <Shield className="h-3 w-3" style={{ color: 'var(--color-blue-600)' }} />,
  member: <UserCheck className="h-3 w-3" style={{ color: 'var(--color-green-400)' }} />,
  viewer: <Eye className="h-3 w-3" style={{ color: 'rgba(255,255,255,0.35)' }} />,
};

const ALL_NAV_HREFS = SIDEBAR_NAV.flatMap(g => g.items).map(i => i.href);

function AppSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const [location, setLocation] = useLocation();
  const { user, logout, can, organization } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const daysLeft = organization?.subscriptionStatus === "trialing" && organization?.trialEndsAt
    ? Math.ceil((new Date(organization.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const showTrialBadge = daysLeft !== null && daysLeft > 0;

  const activeHref = ALL_NAV_HREFS
    .filter(h => h === location || (h !== '/' && location.startsWith(h)))
    .reduce((best, h) => h.length > best.length ? h : best, '');

  if (!user) {
    return (
      <aside
        style={{ width: 210, background: 'var(--color-stone-950)', flexShrink: 0 }}
        className="flex flex-col h-screen sticky top-0"
      >
        <div
          className="flex items-center gap-2.5 px-3 py-3.5"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div
            className="flex items-center justify-center rounded-md flex-shrink-0"
            style={{ width: 30, height: 30, background: 'var(--color-green-700)' }}
          >
            <Ruler className="h-3.5 w-3.5 text-white" />
          </div>
          <span
            className="font-bold text-base text-white tracking-tight"
            style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}
          >
            FenceMetric
          </span>
        </div>
      </aside>
    );
  }

  async function handleLogout() {
    await logout();
    setLocation('/login');
  }

  const initials = user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase();

  return (
    <aside
      style={{ width: 210, background: 'var(--color-stone-950)', flexShrink: 0 }}
      className="flex flex-col h-full sticky top-0"
    >
      {/* Brand */}
      <div
        className="flex items-center gap-2.5 px-3 py-3.5"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div
          className="flex items-center justify-center rounded-md flex-shrink-0"
          style={{ width: 30, height: 30, background: 'var(--color-green-700)' }}
        >
          <Ruler className="h-3.5 w-3.5 text-white" />
        </div>
        <span
          className="font-bold text-base text-white tracking-tight"
          style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}
        >
          FenceMetric
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-2 flex flex-col gap-0">
        {SIDEBAR_NAV.map((group, gi) => (
          <div key={gi} className={gi < SIDEBAR_NAV.length - 1 ? 'mb-1' : ''}>
            <div
              className="px-2.5 py-1.5 text-[9px] font-semibold uppercase"
              style={{ fontFamily: 'var(--font-heading)', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.12em' }}
            >
              {group.label}
            </div>
            {group.items.map(item => {
              if (item.requireCan && !can(item.requireCan)) return null;
              const active = item.href === activeHref;
              return (
                <button
                  key={item.href}
                  onClick={() => { setLocation(item.href); onNavigate?.(); }}
                  aria-label={item.label}
                  aria-current={active ? "page" : undefined}
                  className="flex items-center gap-2.5 w-full rounded-md mb-px text-sm transition-all duration-100"
                  style={{
                    padding: '7px 10px',
                    background: active ? 'var(--color-green-700)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.55)',
                    fontWeight: active ? 500 : 400,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            {gi < SIDEBAR_NAV.length - 1 && (
              <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '4px 2px' }} />
            )}
          </div>
        ))}
      </nav>

      {/* Trial countdown badge */}
      {showTrialBadge && daysLeft !== null && (
        <div className="px-2 py-1.5">
          <button
            onClick={() => { setLocation('/settings?tab=billing'); onNavigate?.(); }}
            className="flex items-center gap-2 w-full rounded-md px-2.5 py-2 text-xs font-medium transition-all"
            style={{
              background: daysLeft <= 3 ? 'rgba(239,68,68,0.12)' : daysLeft <= 7 ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${daysLeft <= 3 ? 'rgba(239,68,68,0.3)' : daysLeft <= 7 ? 'rgba(251,191,36,0.3)' : 'rgba(255,255,255,0.12)'}`,
              color: daysLeft <= 3 ? 'rgb(239,68,68)' : daysLeft <= 7 ? 'rgb(251,191,36)' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
            }}
          >
            <Clock className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left in trial</span>
          </button>
        </div>
      )}

      {/* User footer */}
      <div className="p-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          className="flex items-center gap-2 w-full rounded-md px-2.5 py-2 transition-colors"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <div
            className="flex items-center justify-center rounded-full flex-shrink-0 font-bold text-xs text-white"
            style={{ width: 28, height: 28, background: 'var(--color-green-700)', fontFamily: 'var(--font-heading)' }}
          >
            {initials}
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="text-xs font-medium truncate" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'var(--font-heading)' }}>
              {user.name || user.email}
            </div>
            <div className="flex items-center gap-1 mt-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
              {ROLE_ICONS[user.role]}
              <span className="capitalize">{user.role}</span>
            </div>
          </div>
          <ChevronDown className="h-3 w-3 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }} />
        </button>

        {userMenuOpen && (
          <div
            className="mt-1 rounded-md overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          >
            {can('manage_billing') && (
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-xs transition-colors"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-sans)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                onClick={() => { setUserMenuOpen(false); setLocation('/settings?tab=billing'); onNavigate?.(); }}
              >
                <CreditCard className="h-3.5 w-3.5" />
                Billing
              </button>
            )}
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-xs transition-colors"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,100,100,0.7)', fontFamily: 'var(--font-sans)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              onClick={handleLogout}
              data-testid="button-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { organization, user, loading, refresh } = useAuth();
  const [, setLocation] = useLocation();
  const [repairing, setRepairing] = useState(false);
  const [repairError, setRepairError] = useState<string | null>(null);

  if (loading || !user) return null;

  async function handleRepairOrg() {
    setRepairing(true);
    setRepairError(null);
    try {
      const res = await fetch("/api/auth/repair-my-org", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        await refresh();
      } else {
        const data = await res.json().catch(() => ({}));
        setRepairError(data.message || "Repair failed — try refreshing instead.");
      }
    } catch {
      setRepairError("Network error — please try refreshing the page.");
    } finally {
      setRepairing(false);
    }
  }

  if (!organization) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Unable to load your workspace
        </h2>
        <p className="max-w-sm mb-4" style={{ color: 'var(--color-stone-500)' }}>
          There was a problem loading your account data. You can try re-running the setup process,
          or refresh the page. If the issue persists, contact support at support@fencemetric.com.
        </p>
        {repairError && (
          <p className="text-sm text-red-600 mb-4 max-w-sm">{repairError}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-medium transition-colors hover:opacity-90 disabled:opacity-60"
            disabled={repairing}
            onClick={handleRepairOrg}
          >
            {repairing ? "Running setup…" : "Re-run setup"}
          </button>
          <button
            className="border border-primary text-primary px-6 py-2 rounded-md font-medium transition-colors hover:bg-primary/10"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </button>
        </div>
      </div>
    );
  }

  const isTrialing = organization.subscriptionStatus === "trialing";
  const trialExpired = isTrialing && organization.trialEndsAt && new Date() > new Date(organization.trialEndsAt);
  const isActive = organization.subscriptionStatus === "active";
  const isOwner = user.role === "owner";

  if (!isActive && !isTrialing || trialExpired) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
        <CreditCard className="h-12 w-12 mb-4" style={{ color: 'var(--color-stone-300)' }} />
        <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {trialExpired ? "Trial expired" : "Subscription required"}
        </h2>
        <p className="max-w-sm mb-6" style={{ color: 'var(--color-stone-500)' }}>
          {trialExpired
            ? "Your 14-day trial has ended. Subscribe to continue using FenceMetric."
            : "Your subscription is inactive. Please update your billing to continue."}
        </p>
        {isOwner ? (
          <button
            className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-medium transition-colors hover:opacity-90"
            onClick={() => setLocation("/settings?tab=billing")}
          >
            Manage billing
          </button>
        ) : (
          <p className="text-sm" style={{ color: 'var(--color-stone-400)' }}>Contact your account owner to restore access.</p>
        )}
      </div>
    );
  }

  return <>{children}</>;
}

function RedirectTo({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation(to, { replace: true } as never); }, [to, setLocation]);
  return null;
}

function LoadingSpinner() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Spinner className="size-8" style={{ color: 'var(--color-green-700)' }} />
    </div>
  );
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <RedirectTo to="/login" />;
  return (
    <SubscriptionGate>
      <ErrorBoundary>
        <Dashboard />
      </ErrorBoundary>
    </SubscriptionGate>
  );
}

function Router() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
    <Switch>
      <Route path="/login">
        {() => <ErrorBoundary><Login /></ErrorBoundary>}
      </Route>
      <Route path="/register">
        {() => <ErrorBoundary><Register /></ErrorBoundary>}
      </Route>
      <Route path="/forgot-password">
        {() => <ErrorBoundary><ForgotPassword /></ErrorBoundary>}
      </Route>
      <Route path="/reset-password">
        {() => <ErrorBoundary><ResetPassword /></ErrorBoundary>}
      </Route>
      <Route path="/invite/:token">
        {() => <ErrorBoundary><InvitePage /></ErrorBoundary>}
      </Route>
      <Route path="/client/:id">
        {(params) => <ErrorBoundary><ClientView params={params} /></ErrorBoundary>}
      </Route>
      <Route path="/view/:token">
        {(params: { token: string }) => <ErrorBoundary><SharedPlanPage params={params} /></ErrorBoundary>}
      </Route>
      <Route path="/">
        {() => <LandingOrDashboard />}
      </Route>
      <Route path="/new"><RedirectTo to="/estimate/new" /></Route>
      <Route path="/clients/:id">
        {(params: { id: string }) => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <ClientProfile params={params} />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/clients">
        {() => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <ClientsPage />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/schedule">
        {() => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <SchedulePage />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/catalog">
        {() => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <Catalog />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/settings">
        {() => (
          <RequireAuth>
            <ErrorBoundary>
              <SettingsPage />
            </ErrorBoundary>
          </RequireAuth>
        )}
      </Route>
      <Route path="/settings/team">
        {() => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <TeamPage />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/estimate/:id">
        {(params: { id: string }) => (
          <RequireAuth>
            <SubscriptionGate>
              <ErrorBoundary>
                <Estimate params={params} />
              </ErrorBoundary>
            </SubscriptionGate>
          </RequireAuth>
        )}
      </Route>
      <Route path="/admin">
        {() => <ErrorBoundary><AdminPage /></ErrorBoundary>}
      </Route>
      <Route path="/dev/site-designer/:projectId">
        {(params: { projectId: string }) => (
          <RequireAuth>
            <ErrorBoundary>
              <DevSiteDesigner params={params} />
            </ErrorBoundary>
          </RequireAuth>
        )}
      </Route>
      <Route path="/projects"><RedirectTo to="/" /></Route>
      <Route path="/team"><RedirectTo to="/settings/team" /></Route>
      <Route component={NotFound} />
    </Switch>
    </Suspense>
  );
}

function AppLayout({ heightClass = "h-screen", minHeightClass = "min-h-screen" }: { heightClass?: string; minHeightClass?: string } = {}) {
  const [location] = useLocation();
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isClientView = location.startsWith('/client/') || location === '/client';
  const isSharedView = location.startsWith('/view/');
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location) || location.startsWith('/invite');
  const isAdminPage = location.startsWith('/admin');
  const isProtectedRoute = !isClientView && !isSharedView && !isAuthPage && !isAdminPage;
  const showSidebar = isProtectedRoute && (!!user || (loading && location !== '/'));

  if (showSidebar) {
    return (
      <div className={`flex ${heightClass} overflow-hidden`} style={{ background: 'var(--color-stone-50)' }}>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:rounded focus:shadow-lg focus:text-sm"
        >
          Skip to content
        </a>
        {/* Desktop sidebar — always visible */}
        <div className="hidden md:block">
          <AppSidebar />
        </div>

        {/* Mobile drawer */}
        {isMobile && drawerOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 z-50 md:hidden">
              <AppSidebar onNavigate={() => setDrawerOpen(false)} />
            </div>
          </>
        )}

        <main id="main-content" className="flex-1 overflow-y-auto">
          {/* Mobile top bar */}
          <div className="flex items-center px-4 py-3 border-b border-slate-200 md:hidden sticky top-0 z-30" style={{ background: 'var(--color-stone-50)' }}>
            <button onClick={() => setDrawerOpen(true)} aria-label="Open menu">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <span className="ml-3 font-bold text-sm" style={{ fontFamily: 'var(--font-heading)' }}>FenceMetric</span>
          </div>
          <Router />
        </main>
      </div>
    );
  }

  return (
    <div className={minHeightClass} style={{ background: 'var(--color-stone-50)' }}>
      <Router />
    </div>
  );
}

function ImpersonationBanner({ impersonating }: { impersonating: { userId: number; email: string } }) {
  const { user } = useAuth();
  const [exiting, setExiting] = useState(false);
  const label = user?.name || impersonating.email;

  async function handleExit() {
    setExiting(true);
    try {
      // The admin secret is stashed in sessionStorage by the /admin page and
      // survives the same-tab full-page navigation that impersonation performs.
      // /api/admin/stop-impersonation is CSRF-exempt and authenticated by this
      // header, exactly like every other admin action.
      const secret = sessionStorage.getItem("adminSecret") ?? "";
      await fetch("/api/admin/stop-impersonation", {
        method: "POST",
        headers: { "x-admin-secret": secret, "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch {
      // Navigate away regardless — a full reload guarantees no impersonated
      // state survives in client memory.
    } finally {
      window.location.href = "/login";
    }
  }

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 px-4 py-2 text-sm font-medium text-white"
      style={{ background: '#dc2626', flexShrink: 0 }}
    >
      <Eye className="h-4 w-4 flex-shrink-0" />
      <span>
        Impersonating <strong>{label}</strong>
      </span>
      <button
        onClick={handleExit}
        disabled={exiting}
        data-testid="button-stop-impersonation"
        className="rounded px-2.5 py-0.5 text-xs font-semibold transition-colors disabled:opacity-60"
        style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.5)', cursor: 'pointer' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
      >
        {exiting ? "Exiting…" : "Exit"}
      </button>
    </div>
  );
}

function AppShell() {
  const { impersonating } = useAuth();

  // Not impersonating: render the layout exactly as before so the banner has
  // zero effect on the normal layout (no shift, no overlap).
  if (!impersonating) return <AppLayout />;

  return (
    <div className="flex flex-col h-screen">
      <ImpersonationBanner impersonating={impersonating} />
      <div className="flex-1 min-h-0 overflow-y-auto">
        <AppLayout heightClass="h-full" minHeightClass="min-h-full" />
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Helmet defaultTitle="FenceMetric" titleTemplate="%s — FenceMetric" />
          <AppShell />
          <Toaster position="bottom-center" richColors closeButton />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
